import { useEffect, useState, type ReactNode } from 'react'
import { Wordmark } from './Wordmark'
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom'
import { getInbox, type AuthenticatedSession } from '../api'
import { ThemeToggle } from './ThemeToggle'
import { TrialBanner } from './TrialBanner'
import { orgSwitchTarget } from '../orgSwitch'

/**
 * The mark is a wax seal, which is what the product actually produces and what
 * the marketing site is built around. Its ink is the accent, so it re-points
 * with the theme rather than carrying a colour of its own.
 */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/**
 * Which organization the pane's Organization links work on, for somebody who
 * administers more than one. Choosing another keeps the page: Members of one
 * office goes to Members of the other, and on any other page only the links
 * change (orgSwitchTarget). Its label is shown on the phone's bar, where the
 * pane's "Organization" heading is not.
 */
export function OrgSwitcher({
  administered,
  current,
  onSwitch,
}: {
  administered: AuthenticatedSession['memberships']
  current: string
  onSwitch: (orgId: string) => void
}) {
  return (
    <div className="sidebar-org-switch">
      <label htmlFor="org-switch" className="sidebar-org-switch-label">Organization</label>
      <select
        id="org-switch"
        value={current}
        onChange={(e) => onSwitch(e.target.value)}
      >
        {administered.map((m) => (
          <option key={m.organization_id} value={m.organization_id}>{m.organization_name}</option>
        ))}
      </select>
    </div>
  )
}

export function AppShell({
  session,
  onSignOut,
  signOutError = '',
  children,
}: {
  session: AuthenticatedSession
  onSignOut: () => void
  /** Why the last sign-out was refused; Sign out stays, to try again (review 6, web-3). */
  signOutError?: string
  children: ReactNode
}) {

  const firstOrg = session.memberships[0]
  // The administrative pages take the organization from the URL, so a person
  // in several can move between them and send a colleague a link that lands
  // where they meant.
  const location = useLocation()
  const navigate = useNavigate()

  // How many documents want something from this person right now. Asked once
  // per shell mount rather than polled: it is a signpost, not a notification,
  // and the page it points at is the authority.
  const [waiting, setWaiting] = useState(0)
  useEffect(() => {
    getInbox()
      .then((r) => setWaiting(r.waiting_on_you ?? 0))
      // A shell that cannot count is still a shell. No badge is the right
      // answer to "we do not know", and it is the same answer as "none".
      .catch(() => setWaiting(0))
    // Re-asked on every navigation. A stale OBLIGATION is worse than a stale
    // unread marker: sign the last thing in your post and a badge that never
    // refreshes keeps telling you something needs you until a hard reload.
  }, [location.pathname])
  // Every organization this person administers, and the one the pane's links
  // work on: the one the page is about, when they administer it, and
  // otherwise the first. The links used to point at the first whatever page
  // was open, so somebody who runs two offices reached the second only
  // through the switcher on its settings page, and every link in the pane
  // took them back to the first.
  const administered = session.memberships.filter((m) => m.role === 'admin')
  const routeOrgMatch = location.pathname.match(/^\/orgs\/([^/]+)(?:\/([^/]+))?/)
  // The organization chosen last, in the switcher or by opening one of its
  // pages, so the pane keeps working on it after the person goes to another
  // page (the sending page, say) rather than going back to the first.
  const [picked, setPicked] = useState<string | undefined>(undefined)
  const routeAdmin = administered.find((m) => m.organization_id === routeOrgMatch?.[1])
  if (routeAdmin && routeAdmin.organization_id !== picked) setPicked(routeAdmin.organization_id)
  const adminOf = routeAdmin
    ?? administered.find((m) => m.organization_id === picked)
    ?? administered[0]
  const bannerOrgId = routeOrgMatch?.[1] ?? adminOf?.organization_id
  const bannerMembership = session.memberships.find((m) => m.organization_id === bannerOrgId)
  const bannerBillingIndex = bannerOrgId
    ? session.memberships.findIndex((m) => m.organization_id === bannerOrgId)
    : -1
  const bannerBilling =
    bannerBillingIndex >= 0 && session.billing ? session.billing[bannerBillingIndex] : undefined
  // The organization under the person's name: the one the page or the pane
  // is about, not always the first, which named one office while the pane
  // worked on the other.
  const org = bannerMembership ?? firstOrg
  const name = session.identity.display_name ?? session.identity.email ?? 'User'
  const avatar = initials(name)

  return (
    <div className="app-shell">
      <aside className="app-sidebar">
        {/* Lowercase, with `done` in the accent — the same mark the site uses.
            One wordmark, or the app and the site are two companies. */}
        <Link to="/" className="brand sidebar-logo" aria-label="docsdone, envelopes">
          <Wordmark />
        </Link>

        <div className="sidebar-cta">
          <Link to="/new" className="btn btn-block">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M12 5v14M5 12h14" />
            </svg>
            Send document
          </Link>
        </div>

        <div className="sidebar-section-label">Main</div>
        <nav className="topnav sidebar-nav" aria-label="Main">
          <NavLink to="/" end aria-label="Envelopes">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <span>Envelopes</span>
          </NavLink>
          {/* The count is an obligation, not an unread marker, so it appears
              only when something actually wants this person. A badge that is
              always on is furniture. */}
          <NavLink to="/inbox" aria-label={waiting > 0 ? `Sent to you, ${waiting} waiting` : 'Sent to you'}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M22 12h-6l-2 3h-4l-2-3H2" />
              <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
            </svg>
            <span>Sent to you</span>
            {waiting > 0 && <span className="nav-count">{waiting}</span>}
          </NavLink>
          <NavLink to="/templates" aria-label="Templates">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M4 4h16v16H4z" />
              <path d="M4 9h16M9 4v16" />
            </svg>
            <span>Templates</span>
          </NavLink>
          <NavLink to="/verify" aria-label="Verify">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span>Verify</span>
          </NavLink>
          {/* A full navigation. The docs are HTML beside the app, and a
              client-side route would be caught by the shell and sent home. */}
          <a href="/docs/" aria-label="Docs">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
            </svg>
            <span>Docs</span>
          </a>
        </nav>

        {/* The second group, and only for somebody who can act in it. A pane
            that offers what it will then refuse is worse than one that offers
            nothing: the store answers 403 either way, so showing the link
            teaches a person that the product is broken rather than that they
            lack a permission. */}
        {adminOf && (
          <>
            <div className="sidebar-section-label">Organization</div>
            {administered.length > 1 && (
              <OrgSwitcher
                administered={administered}
                current={adminOf.organization_id}
                onSwitch={(orgId) => {
                  setPicked(orgId)
                  const to = orgSwitchTarget(location.pathname, orgId)
                  if (to) navigate(to)
                }}
              />
            )}
            <nav className="topnav sidebar-nav" aria-label="Organization">
              <NavLink to={`/orgs/${adminOf.organization_id}/settings`} aria-label="Organization settings">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M3 21h18M6 21V8l6-5 6 5v13" />
                  <path d="M10 21v-5h4v5" />
                </svg>
                <span>Settings</span>
              </NavLink>
              <NavLink to={`/orgs/${adminOf.organization_id}/members`} aria-label="Members">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                </svg>
                <span>Members</span>
              </NavLink>
              <NavLink to={`/orgs/${adminOf.organization_id}/recipients`} aria-label="Recipients">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                <span>Recipients</span>
              </NavLink>
              <NavLink to={`/orgs/${adminOf.organization_id}/billing`} aria-label="Billing">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <path d="M2 10h20" />
                </svg>
                <span>Billing</span>
              </NavLink>
              <NavLink to={`/orgs/${adminOf.organization_id}/sign-in`} aria-label="Sign-in">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                  <path d="M10 17l5-5-5-5" />
                  <path d="M15 12H3" />
                </svg>
                <span>Sign-in</span>
              </NavLink>
            </nav>
          </>
        )}

        <div className="sidebar-footer">
          <ThemeToggle />
          {/* The person's name is the way to their account. It was a block of
              its own above a separate Account row, and for somebody who runs
              two organizations the pane then pushed Sign out below a 900px
              window. Its name is what it shows, then "Account": an
              aria-label of "Account" alone hid the visible name from voice
              control (WCAG 2.5.3), and on the rail, where the name is not
              shown, "Account" is what is left. */}
          <NavLink
            to="/account"
            className="sidebar-user"
            title={session.identity.email ?? session.auth_method}
          >
            <span className="user-avatar" aria-hidden>{avatar}</span>
            <span className="sidebar-user-text">
              <span className="sidebar-user-name">{name}</span>
              {org && <span className="sidebar-user-org">{org.organization_name}</span>}
            </span>
            {/* The comma: without it the name read as one run, "Tom Okafor
                Northwind Legal Account" (D7). */}
            <span className="sr-only">, Account</span>
          </NavLink>
          <nav className="sidebar-nav sidebar-nav-footer" aria-label="Account">
            {/* On the phone's bar, where the name above is hidden, Account
                sits beside Sign out, the one other thing a person does to
                their own session. The figure is in a circle, so on that bar
                it is not taken for Contacts, whose figure is not. */}
            <NavLink to="/account" aria-label="Account" className="sidebar-account">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="10" r="3" />
                <path d="M7 20.66V19a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.66" />
              </svg>
              <span>Account</span>
            </NavLink>
            <a
              href="#"
              aria-label="Sign out"
              onClick={(e) => {
                e.preventDefault()
                onSignOut()
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              <span>Sign out</span>
            </a>
          </nav>
        </div>
      </aside>

      <div className="app-body">
        {signOutError && (
          <div className="app-notice-wrap">
            <div id="sign-out-refused" className="notice notice-bad" role="alert">{signOutError}</div>
          </div>
        )}
        {bannerBilling && bannerMembership?.role === 'admin' && (
          <TrialBanner billing={bannerBilling} orgName={bannerMembership.organization_name} />
        )}
        {children}
      </div>
    </div>
  )
}
